import authClient from '@/utils/auth-client'
import router from "@/router";
import { clearAllSessionCaches } from "@/utils/session-cache";

class AuthService {
  public signIn(email: string, password: string) {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    // A new sign-in must not reuse another user's cached, role-dependent lists (e.g. the menu).
    clearAllSessionCaches()

    return new Promise<boolean>((resolve, reject) => {
      const requestBody = 'grant_type=password&scope=API&username=' + email + '&password=' + password

      authClient.post('/token', requestBody).then((res) => {
        const token = res.data.access_token
        const expiresIn = (res.data.expires_in | 0) * 1000

        if (token && token.length && expiresIn > 0) {
          const expirationDate = new Date((new Date()).getTime() + expiresIn)

          localStorage.setItem('access_token', token)
          localStorage.setItem('refresh_token', res.data.refresh_token)

          resolve(true)
        } else {
          reject(new Error('Invalid access token'))
        }
      }).catch((error) => reject(error))
    })
  }

  private refreshing: Promise<boolean> | null = null

  // One refresh at a time: when several requests get a 401 together (a page that fires many
  // calls at once), they all wait on the same refresh instead of each spending the refresh
  // token - a second use of an already-used token fails and signs the user out.
  public refreshToken() {
    if (!this.refreshing) {
      this.refreshing = new Promise<boolean>((resolve, reject) => {
        const requestBody = 'grant_type=refresh_token&refresh_token=' + localStorage.getItem('refresh_token')

        authClient.post('/token', requestBody).then((res) => {
          const token = res.data.access_token
          const expiresIn = (res.data.expires_in | 0) * 1000

          if (token && token.length && expiresIn > 0) {
            localStorage.setItem('access_token', token)
            localStorage.setItem('refresh_token', res.data.refresh_token)

            resolve(true)
          } else {
            throw new Error('Invalid refresh response')
          }
        }).catch((error) => {
          localStorage.clear()
          localStorage.setItem('sessionExpired', "true")
          router.push('/signin')
          reject(error)
        })
      }).then((ok) => {
        this.refreshing = null
        return ok
      }, (error) => {
        this.refreshing = null
        throw error
      })
    }
    return this.refreshing
  }

  public signOut() {
    return new Promise<boolean>((resolve) => {
      localStorage.clear()
      sessionStorage.clear()
      resolve(true)
    })
  }


  public getRoleFromToken(): string | null {
    const token = localStorage.getItem('access_token')
    if (!token) return null
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const authorities: string[] = payload.authorities || []
      if (authorities.includes('ROLE_ADMIN')) return 'ROLE_ADMIN'
      if (authorities.includes('ROLE_SUPPORT')) return 'ROLE_SUPPORT'
      return authorities[0] || null
    } catch {
      return null
    }
  }

  public isAdmin(): boolean {
    return this.getRoleFromToken() === 'ROLE_ADMIN'
  }

  public isSignedIn() : boolean {
    const token = localStorage.getItem('access_token')

    return token !== null && token.length > 0
  }

}

export default new AuthService()
